/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useRef } from "react";
import { isFunction } from "@adapptio/lib-shared";
import { useResizeObserver, UseResizeObserverCallback, useForkRef } from "../hooks";

export interface IResizeObserverProps {
	onResize?: UseResizeObserverCallback;
}

export const ResizeObserver: FC<IResizeObserverProps> = ({ children, onResize }) => {
	const childRef = useRef<HTMLElement>();
	const childs = React.Children.toArray(children);

	if (childs.length > 1) {
		console.error("ResizeObserver: Found more than one child. ResizeObserver works only with one child.");

		return null;
	}

	const child = childs[0];

	if (!React.isValidElement(child)) {
		return null;
	}

	const forkRef = useForkRef(childRef, child["ref"]);

	const handleResize = useCallback<UseResizeObserverCallback>((entry) => {
		if (isFunction(onResize)) {
			onResize(entry, null);
		}
	}, []);

	useResizeObserver(childRef, handleResize);

	return React.cloneElement(child, {
		ref: forkRef
	} as React.HTMLAttributes<unknown>);
};
